/**
 * Milo's face on a telephone — the part that is about the DEVICE rather than about a
 * component.
 *
 * Copied from `pms-web/src/libs/milo.ts` rather than shared: the two portals are two Vue
 * projects with no library between them (the monorepo shares Go, not front ends), and
 * the reception's copy answers a different question at the end. What is copied is copied
 * verbatim, including the reasoning, so the two cannot drift about how the package is
 * loaded.
 *
 * # Phase 1 draws the POSTER, and ships only the poster
 *
 * `MiloHead.vue` and the 3D head are NOT ported yet, on purpose. The reception portal's
 * renderer is being replaced on `parallel/milo-eyes` — our own over `three`, because the
 * owner's eye morph targets cannot be reached through model-viewer at all (CLAUDE.md
 * deviations 28 and 29) — and that branch brings its own version of the asset folder.
 * Porting the old head to a telephone now would mean porting it twice.
 *
 * So `public/milo/1.1.0/` here holds ONE file, the poster, and its own README says why.
 * Everything under `public/` is copied into `dist/` and served, so carrying the rest of
 * the package would put 1.9 MB on a guest's telephone in order to use 148 KB of it — on
 * the one portal in this product that opens on roaming with the data saver on, which is
 * the whole reason `miloHeavyAllowed()` below exists.
 *
 * The picture will not jump when the head arrives: the poster is the same crop the model
 * is framed in, which is what it is in the package for.
 */

/**
 * The folder the head is served from.
 *
 * Versioned, and deliberately so. Caddy answers a real file under `/opt/dist` without a
 * `Cache-Control` header (CLAUDE.md deviation 13 puts the no-store headers on the SPA
 * fallback, which is right), so a browser is free to keep `milo-element.js` and
 * `MILO-Head.glb` for as long as it likes. Files that must agree with each other and are
 * cached independently are chances to run a new renderer against an old model. A new
 * version is a new folder and a one-line change here.
 */
export const MILO_ASSET_BASE = `${import.meta.env.BASE_URL}milo/1.1.0`

/** The poster, resolved against the PAGE rather than against the bundle chunk this code
 *  is running from — the portal is served from the domain root today and this keeps
 *  working if it ever is not. */
export const miloPosterUrl = (): string =>
    new URL(`${MILO_ASSET_BASE}/MILO-Poster.png`, document.baseURI).href

/**
 * The same poster with its ground cut away (alpha), for the BUTTON: an off-white face
 * on the poster's own off-white ground did not separate from it inside the dark disc
 * ("obraz se ne loči od podlage", 7. 9. 2026). Cut from the owner's file, not redrawn —
 * see the folder's README.
 */
export const miloPosterCutoutUrl = (): string =>
    new URL(`${MILO_ASSET_BASE}/MILO-Poster-cutout.png`, document.baseURI).href

/* The reception portal's copy of this file also carries `loadMiloSdk`, which imported
 * the delivered package's web component from a run-time URL. It is deliberately NOT here:
 * the file it imported is not in this portal's folder, so a loader for it could only ever
 * produce a 404, and the head this portal will get is `three` in a chunk of its own
 * rather than a script served out of `public/`. */

/** Asked once per page; the answer cannot change while the tab is open. */
let webglSupported: boolean | undefined

/**
 * Whether this browser can draw the head at all.
 *
 * Asked BEFORE the import rather than after it, so a device without WebGL draws the
 * poster instantly instead of downloading 1.8 MB in order to fail. The canvas is thrown
 * away immediately; the question is only whether a context exists.
 */
export const miloRendererSupported = (): boolean => {
    if (webglSupported !== undefined) return webglSupported

    try {
        const canvas = document.createElement('canvas')

        webglSupported = !!(canvas.getContext('webgl2') ?? canvas.getContext('webgl'))
    } catch {
        webglSupported = false
    }

    return webglSupported
}

/**
 * Whether this device should be handed the 3D head at all.
 *
 * The reception portal asks only about WebGL, because a receptionist's machine is on the
 * hotel's own wire. A guest's telephone is not: this page opens in a car park, on
 * roaming, with the data saver on — and 1.8 MB is a real cost that somebody else pays.
 *
 * The poster is the whole fallback. It is in the package, it is one file, and it is the
 * same crop, so nothing jumps if the model never loads.
 *
 * TODO(parallel/milo-eyes): phase 1 ships the poster and this gate is the switch the head
 * will hang on. When that branch lands, its version of `public/milo/<version>/` is copied
 * in beside this file, `MILO_ASSET_BASE` is moved to it, and `MiloHead.vue` is added as
 * the `heavy` class's content — the mark classes in `MiloButton.vue` are already the
 * three states this returns.
 */
export const miloHeavyAllowed = (): boolean => {
    if (!miloRendererSupported()) return false

    const connection = (navigator as Navigator & {
        connection?: { saveData?: boolean, effectiveType?: string }
    }).connection

    if (connection?.saveData) return false
    if (connection?.effectiveType === '2g' || connection?.effectiveType === 'slow-2g' || connection?.effectiveType === '3g') return false

    return !window.matchMedia('(prefers-reduced-motion: reduce)').matches
}
